import React, { useState, useEffect } from 'react';
import { Users, Plus, Loader2, CheckCircle, XCircle, Shield } from 'lucide-react';
import { usersAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const ROLE_BADGE = {
  super_admin:    'bg-purple-100 text-purple-700',
  operations_mgr: 'bg-blue-100 text-blue-700',
  finance:        'bg-emerald-100 text-emerald-700',
  compliance:     'bg-amber-100 text-amber-700',
  read_only:      'bg-gray-100 text-gray-600',
};

const ROLE_LABELS = {
  super_admin:    'Super Admin',
  operations_mgr: 'Operations Manager',
  finance:        'Finance',
  compliance:     'Compliance',
  read_only:      'Read Only',
};

const F = ({ label, children }) => <div><label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>{children}</div>;
const I = (p) => <input {...p} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30"/>;

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [saving, setSaving]   = useState(false);

  const initForm = { name:'', email:'', password:'', role:'read_only', is_active:1 };
  const [form, setForm] = useState(initForm);

  const load = async () => {
    setLoading(true);
    try { const { data } = await usersAPI.list(); setUsers(data.data.items||data.data); }
    catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditUser(null); setForm(initForm); setModal(true); };
  const openEdit   = (u) => {
    setEditUser(u);
    setForm({ name: u.name, email: u.email, password:'', role: u.role, is_active: u.is_active });
    setModal(true);
  };

  const save = async () => {
    if (!editUser && (!form.password || form.password.length < 8)) { toast.error('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      if (editUser) {
        const payload = { name: form.name, role: form.role, is_active: form.is_active };
        if (form.password) payload.password = form.password;
        await usersAPI.update(editUser.id, payload);
        toast.success('User updated');
      } else {
        await usersAPI.create(form);
        toast.success('User created');
      }
      setModal(false);
      load();
    } catch {} finally { setSaving(false); }
  };

  const deactivate = async (u) => {
    if (u.id === me?.id) { toast.error("You can't deactivate your own account"); return; }
    if (!window.confirm(`Deactivate ${u.name}?`)) return;
    try { await usersAPI.deactivate(u.id); toast.success('User deactivated'); load(); }
    catch {}
  };

  const reactivate = async (u) => {
    try { await usersAPI.update(u.id, { is_active: 1 }); toast.success('User reactivated'); load(); }
    catch {}
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value }));

  return (
    <div className="p-6 max-w-[1000px] mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">User Management</h1>
          <p className="text-sm text-gray-500">{users.filter(u=>u.is_active).length} active users</p>
        </div>
        <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600">
          <Plus size={14}/> Add User
        </button>
      </div>

      {/* Role legend */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Role Permissions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-xs text-gray-600">
          {[['Super Admin','Full access + settings + user management'],
            ['Operations Manager','Assets, maintenance, grid operations'],
            ['Finance','Financial module, invoices, reports'],
            ['Compliance','Regulatory, ESG, carbon modules'],
            ['Read Only','View all, export — no edits'],
          ].map(([role, desc]) => (
            <div key={role} className="flex items-start gap-2 py-1.5">
              <Shield size={12} className="text-primary-500 shrink-0 mt-0.5"/>
              <div><p className="font-medium text-gray-700">{role}</p><p className="text-gray-400">{desc}</p></div>
            </div>
          ))}
        </div>
      </div>

      {/* Users table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-500 text-xs uppercase">
            <tr>{['User','Email','Role','Status','Last Login',''].map(h=>(
              <th key={h} className={clsx('px-4 py-3 font-medium', h===''?'text-right':'text-left')}>{h}</th>
            ))}</tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12"><Loader2 size={20} className="animate-spin text-gray-400 mx-auto"/></td></tr>
            ) : users.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-gray-400 text-sm">No users found.</td></tr>
            ) : users.map((u, i) => (
              <tr key={u.id} className={clsx(i%2===0?'bg-white':'bg-surface', !u.is_active && 'opacity-60')}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                      {u.name[0]?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{u.name} {u.id===me?.id&&<span className="text-xs text-gray-400">(you)</span>}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.email}</td>
                <td className="px-4 py-3">
                  <span className={clsx('px-2 py-0.5 rounded-full text-xs font-medium', ROLE_BADGE[u.role])}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-3">
                  {u.is_active
                    ? <span className="flex items-center gap-1 text-xs text-emerald-700 font-medium"><CheckCircle size={12}/>Active</span>
                    : <span className="flex items-center gap-1 text-xs text-gray-400 font-medium"><XCircle size={12}/>Inactive</span>}
                </td>
                <td className="px-4 py-3 text-gray-500 text-xs">
                  {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString('en-GB') : 'Never'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(u)} className="px-2 py-1 text-xs bg-gray-50 text-gray-600 rounded hover:bg-gray-100">Edit</button>
                    {u.id !== me?.id && (
                      u.is_active
                        ? <button onClick={() => deactivate(u)} className="px-2 py-1 text-xs bg-red-50 text-red-600 rounded hover:bg-red-100">Deactivate</button>
                        : <button onClick={() => reactivate(u)} className="px-2 py-1 text-xs bg-emerald-50 text-emerald-700 rounded hover:bg-emerald-100">Reactivate</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="text-base font-semibold text-gray-900">{editUser ? 'Edit User' : 'Add New User'}</h2>
              <button onClick={() => setModal(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <F label="Full Name *"><I value={form.name} onChange={set('name')} placeholder="Jane Smith"/></F>
              {!editUser && <F label="Email *"><I type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com"/></F>}
              <F label={editUser ? 'New Password (leave blank to keep current)' : 'Password *'}>
                <I type="password" value={form.password} onChange={set('password')} placeholder={editUser ? 'Leave blank to keep current' : 'Min 8 characters'} minLength={editUser ? 0 : 8}/>
              </F>
              <F label="Role">
                <select value={form.role} onChange={set('role')} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500/30 bg-white">
                  {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </F>
              {editUser && (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={!!form.is_active} onChange={set('is_active')} className="rounded"/>
                  <span className="text-sm text-gray-700">Account active</span>
                </label>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-primary-500 text-white text-sm font-medium rounded-lg hover:bg-primary-600 disabled:opacity-60">
                {saving && <Loader2 size={14} className="animate-spin"/>}
                {editUser ? 'Save Changes' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
